import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RouterConstant } from "../../routers/index";
import { SearchExerciseService } from "../../services/search-exercise/search-exercise.service";
import { useLayoutContext } from "../../layouts/general-layout";
import { showErrorToast } from "../../components/Toaster/index";
import { useExerciseStore } from "../../utils/exerciseStore";
import { useUserStore } from "../../utils/userStore";
import { SearchSelectionBoard } from "../../components/search-selection-board";

export default function Index() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialTopicName = location.state?.topicName;
  const { ROUTERS_PATH } = RouterConstant;
  const { t } = useLayoutContext();

  const [topics, setTopics] = useState<{ id: number; name: string; code: number }[]>([]);
  const [levels, setLevels] = useState<{ id: number; name: string; code: number }[]>([]);
  const [difficulties, setDifficulties] = useState<{ id: number; name: string; code: number }[]>([]);
  const [isLevelsLoading, setIsLevelsLoading] = useState<boolean>(false);
  const [isTopicsLoading, setIsTopicsLoading] = useState<boolean>(false);
  const [isDifficultiesLoading, setIsDifficultiesLoading] = useState<boolean>(false);
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<number | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<number | null>(null);

  const exerciseServiceInstance = new SearchExerciseService();
  const sortByCode = (data: any[]) => data.sort((a, b) => a.code - b.code);

  const fetchLevels = async () => {
    setIsLevelsLoading(true);
    try {
      const levelsData = await exerciseServiceInstance.getLevels(true);
      const sortedLevels = sortByCode(levelsData);
      setLevels(sortedLevels);
      if (sortedLevels.length > 0) {
        const profileLevelId = useUserStore.getState().user_attributes["rel_exercise_levels"];
        const matchedLevel = profileLevelId
          ? sortedLevels.find((l) => l.id === Number(profileLevelId))
          : null;
        const defaultLevel = matchedLevel || sortedLevels[0];
        setSelectedLevel(defaultLevel.id);
        fetchTopics(defaultLevel.id);
      }
    } catch (error) {
      console.error("Error fetching levels:", error);
    } finally {
      setIsLevelsLoading(false);
    }
  };

  const fetchTopics = async (levelId: number) => {
    setIsTopicsLoading(true);
    try {
      const topicsData = await exerciseServiceInstance.getTopics(levelId, true);
      const sortedTopics = sortByCode(topicsData);
      setTopics(sortedTopics);
      if (sortedTopics.length > 0) {
        let matchedTopic = null;
        if (initialTopicName) {
          matchedTopic = sortedTopics.find((t) => 
            t.name.toLowerCase().includes(initialTopicName.toLowerCase()) || 
            initialTopicName.toLowerCase().includes(t.name.toLowerCase())
          );
        }
        const defaultTopic = matchedTopic || sortedTopics[0];
        setSelectedTopic(defaultTopic.id);
        fetchDifficulties(levelId, defaultTopic.id);
      } else {
        setSelectedTopic(null);
        setDifficulties([]);
        setSelectedDifficulty(null);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
    } finally {
      setIsTopicsLoading(false);
    }
  };

  const fetchDifficulties = async (levelId: number, topicId: number) => {
    setIsDifficultiesLoading(true);
    try {
      const difficultiesData = await exerciseServiceInstance.getDifficulties(levelId, topicId, true);
      const sortedDifficulties = sortByCode(difficultiesData);
      setDifficulties(sortedDifficulties);
      if (sortedDifficulties.length > 0) {
        const defaultDifficulty = sortedDifficulties[0];
        setSelectedDifficulty(defaultDifficulty.id);
      } else {
        setSelectedDifficulty(null);
      }
    } catch (error) {
      console.error("Error fetching difficulties:", error);
    } finally {
      setIsDifficultiesLoading(false);
    }
  };

  useEffect(() => {
    localStorage.removeItem("selectedExercise");
    fetchLevels();
  }, []);

  const handleSearch = async () => {
    if (!selectedLevel || !selectedTopic || !selectedDifficulty) {
      showErrorToast("נא לבחור רמה ונושא"); // Wait, I should use t here if applicable
      return;
    }

    useExerciseStore.getState().setParams({
      level: selectedLevel,
      topicId: selectedTopic,
      difficultyId: selectedDifficulty,
      offset: 0,
      totalExercise: 0,
    });

    try {
      navigate(ROUTERS_PATH.DISPLAYEXERCISE, {
        state: {
          level: selectedLevel,
          topicId: selectedTopic,
          difficultyId: selectedDifficulty,
          from: "searchExercise",
        },
      });
    } catch (error) {
      console.error("Error searching exercises:", error);
    }
  };

  const handleBackClick = () => {
    navigate(ROUTERS_PATH.USER_MAIN_DASHBOARD);
  };

  const handleLevelSelect = (id: number) => {
    setSelectedLevel(id);
    fetchTopics(id);
  };

  const handleTopicSelect = (id: number) => {
    setSelectedTopic(id);
    if (selectedLevel) fetchDifficulties(selectedLevel, id);
  };

  return (
    <SearchSelectionBoard
      pageTitle={t("searchExerciseTitle")}
      onBackClick={handleBackClick}
      col1Title={t("levelSelection")}
      col2Title={t("chooseTopic")}
      col3Title={t("chooseDifficulty")}
      col1Items={levels}
      col2Items={topics}
      col3Items={difficulties}
      selectedCol1={selectedLevel}
      selectedCol2={selectedTopic}
      selectedCol3={selectedDifficulty}
      onCol1Select={handleLevelSelect}
      onCol2Select={handleTopicSelect}
      onCol3Select={(id) => setSelectedDifficulty(id)}
      isLoadingCol1={isLevelsLoading}
      isLoadingCol2={isTopicsLoading}
      isLoadingCol3={isDifficultiesLoading}
      submitButtonText={t("presentAnExercise")}
      onSubmit={handleSearch}
    />
  );
}
